import React, {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { Button, Checkbox, Divider, Modal, Typography } from '@telus-uds/components-web';
import { Page, GlobalActions, Notice, FrozenBanner } from './css';
import FormAccordion from './formAccordion';
import useAutomationRun from './useAutomationRun';
import catalog from './catalog';

const formatDuration = (ms) => {
  const totalMinutes = Math.round(ms / 60000);
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const Automation = () => {
  const {
    runs, activeRun, queuedRuns, error, startingSectionId, starting, hydrating, batchActive,
    isActive, start, startBatch
  } = useAutomationRun();

  const frozen = isActive || !!startingSectionId || starting || hydrating;
  const [openForms, setOpenForms] = useState([catalog[0].id]);

  // Selection is keyed by section id (unique across the whole catalog), so the
  // same set serves both each form's own checkboxes and the global "select all".
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const allSectionIds = useMemo(
    () => catalog.flatMap((form) => (form.sections || []).map((section) => section.id)),
    []
  );
  const sectionLabelById = useMemo(() => {
    const byId = {};
    catalog.forEach((form) => {
      (form.sections || []).forEach((section) => { byId[section.id] = section.label; });
    });
    return byId;
  }, []);
  const sectionDurationById = useMemo(() => {
    const byId = {};
    catalog.forEach((form) => {
      (form.sections || []).forEach((section) => { byId[section.id] = section.estimatedMs || 0; });
    });
    return byId;
  }, []);
  const allSelected = selectedIds.size > 0 && selectedIds.size === allSectionIds.length;

  const toggleSelected = useCallback((sectionId) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(sectionId)) next.delete(sectionId);
    else next.add(sectionId);
    return next;
  }), []);

  const setSelected = useCallback((sectionIds, isSelected) => setSelectedIds((prev) => {
    const next = new Set(prev);
    sectionIds.forEach((id) => (isSelected ? next.add(id) : next.delete(id)));
    return next;
  }), []);

  // Holds the ids awaiting confirmation so the modal's "Run" button knows what to dispatch.
  const [pendingRunIds, setPendingRunIds] = useState(null);

  const requestRunSelected = useCallback((sectionIds) => {
    if (!sectionIds.length) return;
    setPendingRunIds(sectionIds);
  }, []);

  const cancelRunSelected = useCallback(() => setPendingRunIds(null), []);

  const confirmRunSelected = useCallback(() => {
    if (!pendingRunIds) return;
    startBatch(pendingRunIds);
    setSelected(pendingRunIds, false);
    setPendingRunIds(null);
  }, [pendingRunIds, startBatch, setSelected]);

  const pendingRunTotalMs = useMemo(() => (pendingRunIds || [])
    .reduce((total, id) => total + (sectionDurationById[id] || 0), 0), [pendingRunIds, sectionDurationById]);

  // Kept a primitive so the poll re-creating `activeRun` can't re-open a form the user collapsed.
  const activeFormId = useMemo(() => {
    if (!activeRun) return null;
    const owner = catalog.find((form) => (form.sections || [])
      .some((section) => section.id === activeRun.sectionId));
    return owner ? owner.id : null;
  }, [activeRun]);

  useEffect(() => {
    if (!activeFormId) return;
    setOpenForms((prev) => (prev.includes(activeFormId) ? prev : [...prev, activeFormId]));
  }, [activeFormId]);

  const toggleForm = (id) => setOpenForms((prev) => (
    prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
  ));

  // Spelled out separately (not a nested ternary) so the batch-progress case stays readable.
  const frozenBannerMessage = () => {
    if (!activeRun) return 'Checking for a run in progress…';
    const startedBy = activeRun.triggeredByName ? ` — started by ${activeRun.triggeredByName}` : '';
    if (!queuedRuns.length) {
      return `${activeRun.label} is running${startedBy}. The remaining test cases stay locked until it finishes.`;
    }
    const nextLabel = sectionLabelById[queuedRuns[0].sectionId];
    const remaining = queuedRuns.length === 1 ? '1 test case' : `${queuedRuns.length} test cases`;
    return `${activeRun.label} is running${startedBy}. ${remaining} still queued`
      + `${nextLabel ? ` — up next: "${nextLabel}"` : ''}.`;
  };

  return (
    <Page>
      <Typography heading="h2" variant={{ size: 'h2' }} block>Automation</Typography>
      <Notice>
        Run a test case against the non-prod environment. One test case runs at a time — selecting
        several queues them on the server, so the order survives a page reload.
      </Notice>

      {frozen && (
        <FrozenBanner aria-live="polite">
          {frozenBannerMessage()}
        </FrozenBanner>
      )}

      <GlobalActions>
        <Checkbox
          id="select-all-catalog"
          name="select-all-catalog"
          label="Select all test cases (every form)"
          checked={allSelected}
          inactive={frozen || allSectionIds.length === 0}
          onChange={() => setSelected(allSectionIds, !allSelected)}
        />
        {allSelected && (
          <Button
            variant={{ priority: 'high' }}
            type="button"
            onClick={() => requestRunSelected(allSectionIds.filter((id) => selectedIds.has(id)))}
            inactive={frozen || selectedIds.size === 0}
          >
            {batchActive ? 'Running selected…' : `Run selected (${selectedIds.size})`}
          </Button>
        )}
      </GlobalActions>

      <Divider space={2} variant={{ inverse: true }} />

      {error && <Notice tone="error">{error}</Notice>}

      <Modal
        heading="Run selected test cases?"
        bodyText={`This will run ${(pendingRunIds || []).length} test case${(pendingRunIds || []).length === 1 ? '' : 's'}, with an estimated total time of ${formatDuration(pendingRunTotalMs)}.`}
        confirmButtonText="Run"
        cancelButtonText="Cancel"
        onConfirm={confirmRunSelected}
        onClose={cancelRunSelected}
        isOpen={!!pendingRunIds}
      />

      {catalog.map((form) => (
        <FormAccordion
          key={form.id}
          form={form}
          runs={runs}
          frozen={frozen}
          batchActive={batchActive}
          isBatchOwner={batchActive && activeFormId === form.id}
          startingSectionId={startingSectionId}
          open={openForms.includes(form.id)}
          onToggle={toggleForm}
          onRun={start}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelected}
          onSetSelected={setSelected}
          onRunSelected={requestRunSelected}
        />
      ))}
    </Page>
  );
};

export default Automation;
